/*
 * Replaces native <select> UI with the site dropdown design (see component-dropdown.css).
 * The native select stays in the DOM (visually hidden) and remains the source of truth:
 * choosing an option sets its value and dispatches input/change, so existing listeners,
 * form submission and validation keep working.
 * Opt out with data-native-select on the select.
 */
(function () {
  if (window.RRDropdown) return;

  var SELECTOR = 'select:not([multiple]):not([data-native-select]):not(.choices__input)';
  var ARROW =
    '<svg class="rr-select__arrow" viewBox="0 0 12 8" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1 1.5L6 6.5L11 1.5" stroke="#EE7328" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var uid = 0;
  var openInstance = null;

  function shouldSkip(select) {
    return (
      select.hasAttribute('data-rr-select') ||
      (select.size && select.size > 1) ||
      // Sort by is rendered by Choices.js (styled in component-dropdown.css).
      select.classList.contains('facet-filters__sort') ||
      select.closest('.choices, noscript, .main-select, [data-native-select]')
    );
  }

  function RRSelect(select) {
    this.select = select;
    this.id = 'rr-select-' + ++uid;
    this.activeIndex = -1;
    this.build();
    this.render();
    this.bind();
  }

  RRSelect.prototype.build = function () {
    var select = this.select;
    select.setAttribute('data-rr-select', '');

    this.wrap = document.createElement('div');
    this.wrap.className = 'rr-select';

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.id = this.id + '-toggle';
    this.toggle.className = 'rr-select__toggle';
    this.toggle.setAttribute('aria-haspopup', 'listbox');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-controls', this.id + '-list');
    this.toggle.innerHTML = '<span class="rr-select__value"></span>' + ARROW;
    this.valueEl = this.toggle.querySelector('.rr-select__value');

    this.list = document.createElement('ul');
    this.list.id = this.id + '-list';
    this.list.className = 'rr-select__list';
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;
    this.list.hidden = true;

    var label = select.id ? document.querySelector('label[for="' + CSS.escape(select.id) + '"]') : null;
    if (label) {
      if (!label.id) label.id = this.id + '-label';
      this.toggle.setAttribute('aria-labelledby', label.id + ' ' + this.toggle.id);
      this.list.setAttribute('aria-labelledby', label.id);
    } else {
      var name = select.getAttribute('aria-label') || select.getAttribute('title') || '';
      if (name) {
        this.toggle.setAttribute('aria-label', name);
        this.list.setAttribute('aria-label', name);
      }
    }

    this.wrap.appendChild(this.toggle);
    this.wrap.appendChild(this.list);
    // Inserted before the select so code relying on select.nextElementSibling keeps working.
    select.parentNode.insertBefore(this.wrap, select);
    select.parentNode.classList.add('has-rr-select');
    select.classList.add('rr-select__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
  };

  RRSelect.prototype.render = function () {
    var self = this;
    var select = this.select;
    this.list.innerHTML = '';
    this.options = [];
    this.placeholder = '';

    Array.prototype.forEach.call(select.options, function (opt, index) {
      if (opt.hidden) return;
      if (opt.value === '' && (opt.disabled || index === 0)) {
        self.placeholder = opt.textContent.trim();
        return;
      }
      var li = document.createElement('li');
      li.className = 'rr-select__option';
      li.setAttribute('role', 'option');
      li.id = self.id + '-opt-' + index;
      li.textContent = opt.textContent.trim();
      li._option = opt;
      if (opt.disabled) li.setAttribute('aria-disabled', 'true');
      self.list.appendChild(li);
      self.options.push(li);
    });

    this.toggle.disabled = select.disabled;
    this.sync();
  };

  RRSelect.prototype.sync = function () {
    var selected = this.select.options[this.select.selectedIndex];
    var hasValue = selected && selected.value !== '';
    this.options.forEach(function (li) {
      li.setAttribute('aria-selected', li._option === selected && hasValue ? 'true' : 'false');
    });
    this.valueEl.textContent = hasValue ? selected.textContent.trim() : this.placeholder || (selected ? selected.textContent.trim() : '');
    this.valueEl.classList.toggle('is-placeholder', !hasValue);
  };

  RRSelect.prototype.setActive = function (index) {
    this.options.forEach(function (li, i) {
      li.classList.toggle('is-active', i === index);
    });
    this.activeIndex = index;
    var li = this.options[index];
    if (li) {
      this.toggle.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    } else {
      this.toggle.removeAttribute('aria-activedescendant');
    }
  };

  RRSelect.prototype.open = function () {
    if (this.toggle.disabled) return;
    if (openInstance && openInstance !== this) openInstance.close();
    this.list.hidden = false;
    this.wrap.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    var selected = this.options.findIndex(function (li) {
      return li.getAttribute('aria-selected') === 'true';
    });
    this.setActive(selected > -1 ? selected : this.nextEnabled(-1, 1));
    openInstance = this;
  };

  RRSelect.prototype.close = function () {
    this.list.hidden = true;
    this.wrap.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
    if (openInstance === this) openInstance = null;
  };

  RRSelect.prototype.nextEnabled = function (from, step) {
    var count = this.options.length;
    for (var i = 1; i <= count; i++) {
      var index = (from + step * i + count) % count;
      if (this.options[index].getAttribute('aria-disabled') !== 'true') return index;
    }
    return -1;
  };

  RRSelect.prototype.choose = function (index) {
    var li = this.options[index];
    if (!li || li.getAttribute('aria-disabled') === 'true') return;
    var changed = !li._option.selected;
    li._option.selected = true;
    this.sync();
    this.close();
    this.toggle.focus();
    if (changed) {
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  RRSelect.prototype.bind = function () {
    var self = this;

    this.toggle.addEventListener('click', function () {
      self.wrap.classList.contains('is-open') ? self.close() : self.open();
    });

    this.toggle.addEventListener('keydown', function (e) {
      var isOpen = self.wrap.classList.contains('is-open');
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) return self.open();
          self.setActive(self.nextEnabled(self.activeIndex, e.key === 'ArrowDown' ? 1 : -1));
          break;
        case 'Home':
        case 'End':
          if (!isOpen) return;
          e.preventDefault();
          self.setActive(e.key === 'Home' ? self.nextEnabled(-1, 1) : self.nextEnabled(0, -1));
          break;
        case 'Enter':
        case ' ':
          if (!isOpen) return;
          e.preventDefault();
          self.choose(self.activeIndex);
          break;
        case 'Escape':
          if (!isOpen) return;
          e.preventDefault();
          self.close();
          break;
        case 'Tab':
          if (isOpen) self.close();
          break;
      }
    });

    this.list.addEventListener('mousemove', function (e) {
      var li = e.target.closest('.rr-select__option');
      if (li) self.setActive(self.options.indexOf(li));
    });
    this.list.addEventListener('click', function (e) {
      var li = e.target.closest('.rr-select__option');
      if (li) self.choose(self.options.indexOf(li));
    });

    // Label clicks focus the (hidden) native select; hand focus to the toggle.
    this.select.addEventListener('focus', function () {
      self.toggle.focus();
    });
    this.select.addEventListener('change', function () {
      self.sync();
    });

    // Keep in sync when scripts repopulate options (e.g. country → province) or toggle disabled.
    new MutationObserver(function () {
      self.render();
    }).observe(this.select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  };

  function enhance(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(SELECTOR) && !shouldSkip(scope)) new RRSelect(scope);
    scope.querySelectorAll(SELECTOR).forEach(function (select) {
      if (!shouldSkip(select)) new RRSelect(select);
    });
  }

  document.addEventListener('click', function (e) {
    if (openInstance && !openInstance.wrap.contains(e.target)) openInstance.close();
  });

  function init() {
    enhance(document);
    // Sections re-rendered via the Section Rendering API (filters, variant picker) add new selects.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) enhance(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.RRDropdown = { enhance: enhance };
  // Deferred so libraries that own their selects (Choices.js on sort by) initialise first.
  function deferredInit() {
    setTimeout(init, 0);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', deferredInit);
  } else {
    deferredInit();
  }
})();
